// Drop-in replacement for navigator.geolocation.getCurrentPosition(success, error).
//
// 1st attempt: fast network/Wi-Fi based fix (accepts a cached fix up to 1 min old).
// 2nd attempt: GPS with plenty of time (iOS/Safari can be slow to get a fix).
// A PERMISSION_DENIED error is final immediately (no retry). `onError` is only
// called after the final attempt fails, so callers can safely treat
// error.code === error.PERMISSION_DENIED as "the user really denied access".
//
// Returns a cancel function; after it is called neither callback fires.
//
// While a request is in flight, `isLocating()` is true and subscribers are
// notified, so a single global <LocatingPill /> can show a loader everywhere.
import { useEffect, useRef, useState } from "react";

let inFlight = 0;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn(inFlight > 0));

export const isLocating = () => inFlight > 0;

export const useIsLocating = () => {
  const [locating, setLocating] = useState(isLocating());
  useEffect(() => {
    listeners.add(setLocating);
    setLocating(isLocating());
    return () => {
      listeners.delete(setLocating);
    };
  }, []);
  return locating;
};

export const getPositionWithFallback = (onSuccess, onError) => {
  let cancelled = false;
  let finished = false;

  const finish = () => {
    if (finished) return;
    finished = true;
    inFlight = Math.max(0, inFlight - 1);
    notify();
  };

  const handleSuccess = (pos) => {
    finish();
    if (!cancelled) onSuccess?.(pos);
  };

  const handleFinalError = (error) => {
    finish();
    if (!cancelled) onError?.(error);
  };

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    if (onError) onError({ code: 2, message: "Geolocation not supported", PERMISSION_DENIED: 1, POSITION_UNAVAILABLE: 2, TIMEOUT: 3 });
    return () => {};
  }

  inFlight += 1;
  notify();

  navigator.geolocation.getCurrentPosition(
    handleSuccess,
    (error) => {
      if (cancelled) {
        finish();
        return;
      }
      if (error.code === error.PERMISSION_DENIED) {
        handleFinalError(error);
        return;
      }
      navigator.geolocation.getCurrentPosition(handleSuccess, handleFinalError, {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0,
      });
    },
    {
      enableHighAccuracy: false,
      timeout: 15000,
      maximumAge: 60000,
    }
  );

  return () => {
    cancelled = true;
    finish();
  };
};

export default getPositionWithFallback;

// ---------------------------------------------------------------------------
// Live location-permission watching.
//
// One shared PermissionStatus is watched for the whole app. When the user flips
// the browser's location setting (allow <-> block) every subscribed screen is
// told immediately, with no page refresh. Because some browsers (older Safari)
// don't fire `change` reliably, the state is also re-checked when the tab/window
// regains focus.
// ---------------------------------------------------------------------------
const permissionListeners = new Set();
let permissionState = null;
let watching = false;

const setPermissionState = (next) => {
  if (!next || next === permissionState) return;
  const prev = permissionState;
  permissionState = next;
  // First read just records the baseline; only real changes are broadcast.
  if (prev === null) return;
  permissionListeners.forEach((fn) => fn(next, prev));
};

const recheckPermission = () => {
  if (!navigator.permissions?.query) return;
  navigator.permissions
    .query({ name: "geolocation" })
    .then((status) => setPermissionState(status.state))
    .catch(() => {});
};

const startWatchingPermission = () => {
  if (watching || typeof navigator === "undefined" || !navigator.permissions?.query) return;
  watching = true;
  navigator.permissions
    .query({ name: "geolocation" })
    .then((status) => {
      setPermissionState(status.state);
      status.onchange = () => setPermissionState(status.state);
    })
    .catch(() => {
      watching = false;
    });
  window.addEventListener("focus", recheckPermission);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) recheckPermission();
  });
};

// Calls onGranted() when permission becomes "granted" and onDenied() when it
// becomes "denied", while `enabled` is true (pass a drawer's `open` flag so a
// closed drawer doesn't react). Callbacks always see the latest render's state.
export const useLocationPermissionSync = ({ enabled = true, onGranted, onDenied } = {}) => {
  const handlers = useRef({});
  handlers.current = { enabled, onGranted, onDenied };

  useEffect(() => {
    startWatchingPermission();
    const listener = (state) => {
      const { enabled: on, onGranted: granted, onDenied: denied } = handlers.current;
      if (!on) return;
      if (state === "granted") granted?.();
      else if (state === "denied") denied?.();
    };
    permissionListeners.add(listener);
    return () => {
      permissionListeners.delete(listener);
    };
  }, []);
};
